import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button";
import { useEffect, useState } from "react";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import type { SurfaceSolutionTag } from "../api/types";
import { getAcceptReadiness } from "../utils/mergeTagIds";
import PromoteSurfaceAcceptFields from "./PromoteSurfaceAcceptFields";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  selectionCount: number;
  busy?: boolean;
  onConfirm: (payload: {
    solutionTagIds: number[];
    temporalTagId: number | null;
  }) => void | Promise<void>;
};

export default function PromoteSurfaceBulkAcceptSheet({
  open,
  onOpenChange,
  tags,
  temporalTags,
  selectionCount,
  busy,
  onConfirm,
}: Props) {
  const [selected, setSelected] = useState<number[]>([]);
  const [temporalTagId, setTemporalTagId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setTemporalTagId(temporalTags[0]?.id ?? null);
    }
  }, [open, temporalTags]);

  const { canAccept } = getAcceptReadiness({
    solutionTagIds: selected,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setSelected([]);
          setTemporalTagId(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 pr-12">
          <DialogTitle>Accept {selectionCount} assets</DialogTitle>
          <DialogDescription>
            Choose a temporal phase and solution tags to apply to every selected asset. Other
            existing tags are kept.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-4 overflow-y-auto px-swivu-base py-4">
          <PromoteSurfaceAcceptFields
            tags={tags}
            temporalTags={temporalTags}
            solutionTagIds={selected}
            onSolutionTagIdsChange={setSelected}
            temporalTagId={temporalTagId}
            onTemporalTagIdChange={setTemporalTagId}
            disabled={Boolean(busy)}
            showInlineHints
          />
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || !canAccept}
            onClick={() => onConfirm({ solutionTagIds: selected, temporalTagId })}
          >
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
